import { MediaDocument, MediaDocumentData, MediaImageData, NodePageData, TextMarginalDocument } from '@generated/content';
import { Card, CmsComponentConfig } from '@interfaces/config';
import { CmsComponentNodeData, TImageConfig } from '@models/interfaces';

import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';
import { DocumentResolver } from '@services/resolvers/auxiliary';
import { HtmlHelper } from '@lib/utils/helpers';
import { HyphenatePipe } from '../../../../pipes/hyphenate/hyphenate.pipe';

import { CmsMediaTypeEnum } from '@models/enums';

export class CardComponentResolver {
  static instance: CardComponentResolver;
  static nodePageData: CmsComponentNodeData[] = [];
  static readonly componentType = Card.TypeEnum;

  static getInstance(): CardComponentResolver {
    return (CardComponentResolver.instance ||= new CardComponentResolver());
  }

  /*
   * Returns a CardConfig for a given input (NodePageData or Document1)
   */
  static getCardConfig<T extends NodePageData | MediaDocument | TextMarginalDocument>(
    node: T,
    componentNodeData: CmsComponentNodeData[] = null
  ): Card.ComponentConfig | null {
    componentNodeData ||= this.nodePageData;

    // DocumentsDataInner | TextMarginalDocument
    if ('data' in node) {
      return this.resolveComponentConfigFromDocument((node as MediaDocument).data, componentNodeData)?.config;
    }

    // NodePageData
    return this.resolveComponentConfigFromNodePage(node as NodePageData, componentNodeData)?.config;
  }

  static resolveComponentConfigFromDocument(
    documentNodeReference: MediaDocumentData,
    componentNodePageData: CmsComponentNodeData[] = []
  ): CmsComponentConfig<Card.ComponentConfig> {
    const { cardConfig, documentNode } = this.getMediaDocumentConfig(documentNodeReference.id, componentNodePageData);

    if (!(cardConfig && documentNode)) {
      return null;
    }

    const fileNode = DocumentResolver.getFileNode(
      documentNode.relationships.field_media_document?.data?.id,
      componentNodePageData
    );

    if (!fileNode) {
      return null;
    }

    const imageNodeId: string = documentNode.relationships?.field_image_reference?.data?.id;
    const imageConfig: TImageConfig = this.getCardImageConfig(imageNodeId, componentNodePageData);
    const { href, target } = DocumentResolver.buildDocumentTarget(documentNode.id);

    // Use this to disable gated assets
    // const gated = null;

    // Use this to enable gated assets
    const gated = documentNode.attributes?.field_select_gated_form ?? null;

    const config: Card.ComponentConfig = {
      ...cardConfig,
      imageConfig,
      sticky: false, // currently only used in KC
      link: { href, gated, target, fileName: documentNode.attributes.name },
    } as Card.ComponentConfig;

    return { componentType: this.componentType, config };
  }

  static resolveComponentConfigFromNodePage(
    node: NodePageData,
    componentNodePageData: CmsComponentNodeData[] = []
  ): CmsComponentConfig<Card.ComponentConfig> | null {
 
    const headline: string = node.attributes.field_teaser_headline_page || node.attributes.title;
    const excerpt: string = node.attributes.field_teaser_subheadline_page;

    const config: Card.ComponentConfig = {
      nodeId: BaseComponentResolver.getNodeId(node),
      headline: HtmlHelper.sanitizeControlChars(headline, true, true),
      excerpt: HtmlHelper.sanitizeControlChars(excerpt, true, true),
      link: null,
    };

    const href: string = `/${node.attributes?.path?.langcode}${node.attributes?.path?.alias}`;

    if (href) {
      config.link = { href, text: this.hyphenate(node.attributes?.field_teaser_link_page?.title) };
    }

    const imageId: string | null = node.relationships?.field_teaser_image_page?.data?.id;
    const imageConfig = this.getCardImageConfig(imageId, componentNodePageData);

    if (imageConfig) {
      config.imageConfig = imageConfig;
    }
  
    return { componentType: this.componentType, config };
  }

  static resolveComponentConfigs(
    nodes: MediaDocumentData[] = [],
    nodePageData: CmsComponentNodeData[] = []
  ): Card.ComponentConfig[] {
    if (!nodes?.length) {
      return null;
    }

    return nodes.reduce((p, c) => {
      const config = this.resolveComponentConfigFromDocument(c, nodePageData)?.config;
      return config ? [...p, config] : p;
    }, []);
  }

  static getMediaDocumentConfig(
    documentNodeId: string,
    componentNodePageData: CmsComponentNodeData[] = []
  ): { cardConfig: Partial<Card.ComponentConfig>; documentNode: MediaDocumentData } {
    const documentNode = DocumentResolver.getDocumentNode(documentNodeId, componentNodePageData);

    if (!documentNode) {
   
      return { cardConfig: null, documentNode: null };
    }


    const { field_publishing_date, field_read_time, field_document_type, field_document_description,field_description, field_title, name } =
      documentNode.attributes;

      let excerpt = field_document_description? field_document_description.value:field_description;

     
    const publishedAt: Date | null = field_publishing_date ? new Date(field_publishing_date) : null;
    const readTime: string = field_read_time;

    return {
      cardConfig: {
        taxonomy: field_document_type,
        headline: this.hyphenate(field_title || name),
        excerpt: this.hyphenate(excerpt),
        metas: { publishedAt, readTime },
      },
      documentNode,
    };
  }

  static getCardImageConfig(imageId: string = null, componentNodePageData: CmsComponentNodeData[] = []): TImageConfig {
    if (!imageId) {
      return null;
    }

    const imageNode = BaseComponentResolver.getNode<MediaImageData>(
      imageId,
      MediaImageData.TypeEnum.MediaImage,
      componentNodePageData
    );

    if (!imageNode) {
      return null;
    }

    let { dataUrls, metas } =
      BaseComponentResolver.getImageConfig(imageNode.id, CmsMediaTypeEnum.Image, componentNodePageData) ?? null;

    return { dataUrls, metas, sizes: '25vw' };
  }

  static hyphenate(string: string): string {
    return HyphenatePipe.transform(string);
  }
}
